/**
 * Synthetic ("fake door") relevancy percentage for the promoted related grid.
 *
 * The related works arrive already ordered by descending relatedness
 * (RelatedWorks::Query sorts by the BERT rank; see Liswp::LadderAssignment /
 * the related-works pipeline), but the underlying similarity SCORES are not
 * persisted — the BERT job writes only the ranked ids to DynamoDB. Rather than
 * block this variant on an external pipeline change to persist real scores, we
 * paint a plausible percentage derived purely from the card's rank.
 *
 * This is directionally honest — "more related papers appear first" is true,
 * because the ordering is the real BERT ranking; only the exact percentage is
 * synthetic. When real scores are later persisted, swap this for the real
 * score with no change to the experiment design or the card markup.
 *
 * The value is:
 *  - deterministic (same rank → same percentage, so it never flickers),
 *  - floored at 80 (nothing promoted as related should read as a coin flip),
 *  - strictly monotonically decreasing in rank down to the floor (a lower %
 *    can never sit above a higher one, which is what would make it read as
 *    fake), via a gently accelerating decay tuned so the floor is reached
 *    exactly at rank 7 — the last card of the 8-card related grid.
 */
const TOP_PERCENT = 98;
const FLOOR_PERCENT = 80;

export function syntheticRelevancy(rank: number): number {
  // Linear + small quadratic term: steps widen slightly with rank (2,2,3,2,3…)
  // so the sequence reads organic rather than a fixed arithmetic step, while
  // the strictly-increasing subtraction guarantees monotonicity above the
  // floor.
  const dropped = Math.round(2 * rank + 0.09 * rank * rank);
  return Math.max(TOP_PERCENT - dropped, FLOOR_PERCENT);
}
